import React, { memo } from "react";

import { Grid } from "@material-ui/core";
import { useField } from "formik";
import * as Yup from "yup";

import { Input, Switch, withNamespace } from "components/Form/FormikControls";

import { getAusPaymentsNetworkBsbs } from "constants/ausPaymentsNetworkBsbs";
import { InformationPanel } from "components/Layout";

type BSBSixDigit = string;
type BSBMnemonic = string;
type BSBName = string;

type BSBEntry = [BSBSixDigit, BSBMnemonic, BSBName];

const BSB_LOOKUP = getAusPaymentsNetworkBsbs().reduce(
  (acc, bsbEntry) => {
    acc[bsbEntry[0]] = bsbEntry;
    return acc;
  },
  {} as Record<BSBSixDigit, BSBEntry>,
);

export function normaliseBsb(bsb: string | null): string {
  return (bsb || "").replace(/[^\d]/g, "");
}

function bsbValueTransformer(value: string | null) {
  const valueNormalised = normaliseBsb(value);
  const bsbPart1 = valueNormalised.slice(0, 3);
  const bsbPart2 = valueNormalised.slice(3, 6);

  return bsbPart2
    ? // Return both of the parts combined with the the hyphen separator
      `${bsbPart1}-${bsbPart2}`
    : // Return just the first part of the BSB, without the hyphen,
      // unless that was the last character entered by the user.
      // This provides a more comfortable experience when deleting/editing
      // character around the hyphen.
      `${bsbPart1}${value.slice(-1) === "-" ? "-" : ""}`;
}

function getBsbEntryHelpText(bsbEntry: BSBEntry | null): string {
  if (!bsbEntry) {
    return "";
  }
  const mnemonic = bsbEntry[1];
  const bsbName = bsbEntry[1];
  return [mnemonic, bsbName].filter(Boolean).join(", ");
}

function checkIsRegistered(value: string | null): boolean {
  return Boolean(BSB_LOOKUP[normaliseBsb(value)]) || value === undefined;
}

export const bsbValidation = Yup.string()
  .nullable()
  .test("is-registered", "That BSB is not recognised", checkIsRegistered);

export const accountNumberValidation = Yup.string().nullable();

export const accountNameValidation = Yup.string().nullable();
export const payidValidation = Yup.string().nullable();
export const chequePayableToValidation = Yup.string().nullable();

export const BankingDetailsValidationSchema = Yup.object().shape({
  bsb: bsbValidation,
  accountNumber: accountNumberValidation,
  accountName: accountNameValidation,
  payid: payidValidation,
  chequePayableTo: chequePayableToValidation,
});
interface BsbInputProps {
  disabled?: boolean;
  label: string;
  name: string;
}

export function BsbInput(props: BsbInputProps) {
  const { disabled, label, name } = props;

  const [{ value: bsb }] = useField(name);

  const bsbNormalised = normaliseBsb(bsb);

  const bsbEntry =
    bsbNormalised.length === 6 ? BSB_LOOKUP[bsbNormalised] : null;

  return (
    <Input
      disabled={disabled}
      helpText={getBsbEntryHelpText(bsbEntry)}
      label={label}
      maxLength={7}
      name={name}
      valueTransformer={bsbValueTransformer}
    />
  );
}

interface BankingDetailsFormProps {
  namespace?: string;
  includeHobbyFarmer?: boolean;
  xeroBankingLocked?: boolean;
}

function BankingDetailsFormComponent(props: BankingDetailsFormProps) {
  const {
    namespace: ns,
    includeHobbyFarmer = true,
    xeroBankingLocked = false,
  } = props;
  const disabled = xeroBankingLocked;
  return (
    <>
      {xeroBankingLocked && (
        <Grid item xs={12}>
          <InformationPanel>
            Banking details can only be updated in Xero.
          </InformationPanel>
        </Grid>
      )}

      <Grid item xs={12} sm={6} md={4}>
        <BsbInput
          disabled={disabled}
          label="BSB"
          name={withNamespace(ns, "bsb")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <Input
          disabled={disabled}
          label="Account Number"
          maxLength={20}
          name={withNamespace(ns, "accountNumber")}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Input
          disabled={disabled}
          label="Account Name"
          maxLength={50}
          name={withNamespace(ns, "accountName")}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <Input
          disabled={disabled}
          label="PayID"
          maxLength={50}
          name={withNamespace(ns, "payid")}
        />
      </Grid>
      <Grid item xs={10} md={5}>
        <Input
          disabled={disabled}
          label="Cheque Payable To"
          maxLength={255}
          name={withNamespace(ns, "chequePayableTo")}
        />
      </Grid>
      {includeHobbyFarmer && (
        <Grid item xs={2} md={2}>
          <Switch
            label="Hobby Farmer"
            name="isHobbyFarmer"
            tooltip="When set this business will be flagged to not charge GST by default."
            colProps={{ alignCenter: true }}
          />
        </Grid>
      )}
    </>
  );
}

export const BankingDetailsForm = memo(BankingDetailsFormComponent);
